import { kOrgBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { z } from 'zod'

const InvitePreviewSchema = z.object({
  organization_name: z.string(),
})

export type InvitePreview = z.infer<typeof InvitePreviewSchema>

function getErrorDetail(data: unknown): unknown {
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as { detail?: unknown }).detail : undefined
}

export async function getInvitePreview(inviteId: string): Promise<InvitePreview> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kOrgBasePath}/invite/${inviteId}/preview`
    const response = await fetch(apiUrl, requestOptions)
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? undefined : data
      const detail = getErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })

      throw new ApiError(response.status, `Fetch error - status: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Invite preview response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            parseError: formatUnknownError(parseError),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid invite preview response JSON')
    }

    const parsed = InvitePreviewSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Invite preview response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid invite preview response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    // Skip api errors already captured above
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
